import { z } from 'zod';
import { EMPLOYEE_DOCUMENT_KINDS, EMPLOYEE_DUE_KINDS, EMPLOYMENT_TYPES, PAY_SCHEMES } from '@bulk/types';
import { dateOnlySchema, idSchema, patchOf } from './common';

const personalIdSchema = z
  .string()
  .trim()
  .regex(/^\d{11}$/, 'A Georgian personal number has 11 digits');
const ibanSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/\s+/g, '').toUpperCase())
  .pipe(z.string().regex(/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/, 'Not an IBAN'));

export const createEmployeeSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  email: z.email().toLowerCase().nullable().optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  personalId: personalIdSchema.nullable().optional(),
  dateOfBirth: dateOnlySchema.nullable().optional().describe('Seen only by the person and by those who read employee records; colleagues see only that it is their birthday'),
  emergencyContactName: z.string().trim().max(120).nullable().optional(),
  emergencyContactPhone: z.string().trim().max(32).nullable().optional(),
  position: z.string().trim().max(80).nullable().optional(),
  departmentId: idSchema.nullable().optional(),
  locationId: idSchema.nullable().optional().describe("The branch someone without a login works at, for whoever decides their leave; with a login, the login's branches count. Null: every branch"),
  employmentType: z.enum(EMPLOYMENT_TYPES).default('FULL_TIME'),
  employmentStartDate: dateOnlySchema,
  employmentEndDate: dateOnlySchema.nullable().optional(),
  probationEndDate: dateOnlySchema.nullable().optional().describe('The last day of the probation; whoever manages the team is reminded 14 and 3 days before'),
  contractEndDate: dateOnlySchema.nullable().optional().describe('The last day of a fixed-term contract; whoever manages the team is reminded 14 and 3 days before'),
  annualAllowanceDays: z.number().int().min(0).max(365).default(24),
  extraAllowanceDays: z.number().int().min(0).max(365).default(0),
  pensionParticipant: z.boolean().default(true),
  bankIban: ibanSchema.nullable().optional(),
  userId: idSchema.nullable().optional().describe('A staff login at this gym to link'),
  trainerId: idSchema.nullable().optional().describe("A trainer profile at this gym to link (per-session pay counts that trainer's classes)"),
  memberId: idSchema.nullable().optional().describe("The member record holding this person's cards, so a tap on a staff clock reader clocks them in or out"),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type CreateEmployeeInput = z.infer<typeof createEmployeeSchema>;
export const updateEmployeeSchema = patchOf(createEmployeeSchema).extend({ active: z.boolean().optional() });
export type UpdateEmployeeInput = z.infer<typeof updateEmployeeSchema>;
export const updateMyEmployeeSchema = z.object({ dateOfBirth: dateOnlySchema.nullable() });
export type UpdateMyEmployeeInput = z.infer<typeof updateMyEmployeeSchema>;

export const compensationSchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  scheme: z.enum(PAY_SCHEMES),
  amountMinor: z.number().int(),
  currency: z.string(),
  effectiveFrom: z.string(),
  effectiveTo: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type CompensationDto = z.infer<typeof compensationSchema>;

export const createCompensationSchema = z.object({
  scheme: z.enum(PAY_SCHEMES),
  amountMinor: z.number().int().min(0).max(100_000_000).describe('Monthly salary, hourly rate or rate per class taught, in minor units'),
  effectiveFrom: dateOnlySchema,
  note: z.string().trim().max(300).nullable().optional(),
});
export type CreateCompensationInput = z.infer<typeof createCompensationSchema>;

export const employeeSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  userId: idSchema.nullable(),
  userEmail: z.string().nullable(),
  trainerId: idSchema.nullable(),
  trainerName: z.string().nullable(),
  memberId: idSchema.nullable(),
  memberName: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  personalId: z.string().nullable().describe('Masked to the last four digits unless the caller has employees.pay.read or it is their own record'),
  dateOfBirth: z.string().nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactPhone: z.string().nullable(),
  position: z.string().nullable(),
  departmentId: idSchema.nullable(),
  departmentName: z.string().nullable(),
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  employmentType: z.enum(EMPLOYMENT_TYPES),
  employmentStartDate: z.string(),
  employmentEndDate: z.string().nullable(),
  probationEndDate: z.string().nullable(),
  contractEndDate: z.string().nullable(),
  annualAllowanceDays: z.number().int(),
  extraAllowanceDays: z.number().int(),
  pensionParticipant: z.boolean(),
  bankIban: z.string().nullable().describe('Null unless the caller has employees.pay.read or it is their own record'),
  active: z.boolean(),
  notes: z.string().nullable(),
  compensation: compensationSchema.nullable().describe('Pay terms in force today; null without employees.pay.read (except on your own record)'),
  createdAt: z.string(),
});
export type EmployeeDto = z.infer<typeof employeeSchema>;

export const employeeListQuerySchema = z.object({
  search: z.string().trim().max(120).optional(),
  departmentId: idSchema.optional(),
  status: z.enum(['active', 'inactive', 'all']).default('active'),
});
export type EmployeeListQuery = z.infer<typeof employeeListQuerySchema>;

export const employeeCandidateSchema = z.object({
  kind: z.enum(['STAFF', 'TRAINER']),
  userId: idSchema.nullable(),
  trainerId: idSchema.nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  role: z.string().nullable(),
});
export type EmployeeCandidateDto = z.infer<typeof employeeCandidateSchema>;

export const createDepartmentSchema = z.object({
  name: z.string().trim().min(1).max(60),
  nameLocalized: z.record(z.string(), z.string().trim().max(60)).default({}),
  maxSimultaneousLeave: z.number().int().min(1).max(100).default(2).describe('How many people in the department may be off at once before approvers see a warning'),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>;
export const updateDepartmentSchema = patchOf(createDepartmentSchema);
export type UpdateDepartmentInput = z.infer<typeof updateDepartmentSchema>;

export const departmentSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  maxSimultaneousLeave: z.number().int(),
  sortOrder: z.number().int(),
  employeeCount: z.number().int(),
});
export type DepartmentDto = z.infer<typeof departmentSchema>;

export const createEmployeeDocumentSchema = z
  .object({
    kind: z.enum(EMPLOYEE_DOCUMENT_KINDS),
    title: z.string().trim().min(1).max(120),
    issuedOn: dateOnlySchema.optional(),
    expiresOn: dateOnlySchema.optional().describe('Whoever manages the team is reminded 30 and 7 days before, and the employee too'),
    notes: z.string().trim().max(1000).optional(),
  })
  .refine((v) => !v.issuedOn || !v.expiresOn || v.expiresOn >= v.issuedOn, { path: ['expiresOn'], message: 'A document cannot expire before it was issued' });
export type CreateEmployeeDocumentInput = z.infer<typeof createEmployeeDocumentSchema>;

export const employeeDocumentSchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  kind: z.enum(EMPLOYEE_DOCUMENT_KINDS),
  title: z.string(),
  issuedOn: z.string().nullable(),
  expiresOn: z.string().nullable(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  notes: z.string().nullable(),
  uploadedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type EmployeeDocumentDto = z.infer<typeof employeeDocumentSchema>;

export const employeeDocumentLinkSchema = z.object({ url: z.string(), fileName: z.string(), mimeType: z.string() });
export type EmployeeDocumentLinkDto = z.infer<typeof employeeDocumentLinkSchema>;

export const employeeDueQuerySchema = z.object({ days: z.coerce.number().int().min(1).max(365).default(30) });
export type EmployeeDueQuery = z.infer<typeof employeeDueQuerySchema>;

export const employeeDueItemSchema = z.object({
  kind: z.enum(EMPLOYEE_DUE_KINDS),
  employeeId: idSchema,
  employeeName: z.string(),
  documentId: idSchema.nullable(),
  documentKind: z.enum(EMPLOYEE_DOCUMENT_KINDS).nullable(),
  title: z.string().nullable(),
  date: z.string(),
  daysLeft: z.number().int().describe('Days from today in the gym; negative once the day has passed'),
});
export type EmployeeDueItemDto = z.infer<typeof employeeDueItemSchema>;
